import React, { useEffect, useState } from "react";
import { WifiOff } from "lucide-react";
import { useI18n } from "@/lib/i18n";

/**
 * Slim yellow banner that appears at the top of every Layout while the
 * browser reports `navigator.onLine === false`. On mobile PWAs this
 * doubles as a hint that any read-only content shown was pulled from the
 * offline cache (see /app/frontend/src/lib/offlineCache.js).
 *
 * We deliberately do NOT block writes here — the auth screens, chat, and
 * finance flows will surface their own error toasts if they try to POST
 * while offline. This banner is purely informational.
 */
export default function OfflineBanner() {
  const { t } = useI18n();
  const [online, setOnline] = useState(
    typeof navigator !== "undefined" ? navigator.onLine : true,
  );
  useEffect(() => {
    const on = () => setOnline(true);
    const off = () => setOnline(false);
    window.addEventListener("online", on);
    window.addEventListener("offline", off);
    return () => {
      window.removeEventListener("online", on);
      window.removeEventListener("offline", off);
    };
  }, []);
  if (online) return null;
  return (
    <div
      data-testid="offline-banner"
      className="sticky top-0 z-40 flex items-center justify-center gap-2 bg-amber-100 border-b border-amber-200 text-amber-900 text-xs px-3 py-1.5"
      role="status"
      aria-live="polite"
    >
      <WifiOff className="w-3.5 h-3.5 shrink-0" />
      <span className="font-medium">{t("offlineBanner")}</span>
    </div>
  );
}
